import { useState, useEffect } from "react";
import type { Recommendation, EpisodeInfo } from "../interfaces";
import { categoryItems } from "../helpers";
import { getRecommendationsByEpisode } from "../api-client";
import {
  Table,
  Box,
  Flex,
  Text,
  Spinner,
  Link,
  Tag,
  Wrap,
  Heading,
} from "@chakra-ui/react";

interface EpisodePageProps {
  episodeNumber: string;
}

export function EpisodePage({ episodeNumber }: EpisodePageProps) {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [episodeDetails, setEpisodeDetails] = useState<EpisodeInfo | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEpisodeData = async () => {
      if (!episodeNumber) return;

      setLoading(true);
      setError(null);
      setRecommendations([]);
      setEpisodeDetails(null);

      try {
        const data = await getRecommendationsByEpisode(parseInt(episodeNumber, 10));
        if (data.episode) {
          setEpisodeDetails(data.episode);
          setRecommendations(data.recommendations);
        } else {
          throw new Error(`Episode ${episodeNumber} not found.`);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEpisodeData();
  }, [episodeNumber]);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={10}>
        <Heading size="lg" color="red.500">
          Error
        </Heading>
        <Text mt={4}>{error}</Text>
      </Box>
    );
  }

  if (!episodeDetails) {
    return (
      <Text textAlign="center" py={10}>
        No details found for this episode.
      </Text>
    );
  }

  return (
    <Box w="full" maxW="4xl" mx="auto" my={8}>
      <Box mb={6} p={5} borderWidth="1px" borderRadius="lg">
        <Heading as="h1" size="xl" mb={2}>
          {episodeDetails.title}
        </Heading>
        <Text color="gray.500" fontSize="sm" mb={3}>
          {new Date(episodeDetails.date).toLocaleDateString()} &bull;{" "}
          {Math.round(episodeDetails.length / 60)} mins
        </Text>
        <Text>{episodeDetails.summary}</Text>
      </Box>

      <Heading as="h2" size="lg" mb={4}>
        Recommendations
      </Heading>
      <Table.Root size="lg" interactive colorPalette="green">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Title</Table.ColumnHeader>
            <Table.ColumnHeader>Category</Table.ColumnHeader>
            <Table.ColumnHeader>Topics</Table.ColumnHeader>
            <Table.ColumnHeader>Notes</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {recommendations.length > 0 ? (
            recommendations.map((rec) => (
              <Table.Row key={rec.id}>
                <Table.Cell>
                  <Link href={rec.url} target="_blank">
                    {rec.title}
                  </Link>
                </Table.Cell>
                <Table.Cell>
                  <Flex align="center" gap={2}>
                    {(() => {
                      const Icon = categoryItems.find(
                        (item) => item.value === rec.category,
                      )?.icon;
                      return Icon ? <Icon /> : null;
                    })()}
                    {rec.category}
                  </Flex>
                </Table.Cell>
                <Table.Cell>
                  {(() => {
                    try {
                      const topics = rec.topics ? JSON.parse(rec.topics) : [];
                      if (topics.length > 0) {
                        return (
                          <Wrap>
                            {topics.map((topic: string) => (
                              <Link
                                key={topic}
                                href={`/search?q=${encodeURIComponent(
                                  `topics:"${topic}"`,
                                )}`}
                              >
                                <Tag.Root>
                                  <Tag.Label>{topic}</Tag.Label>
                                </Tag.Root>
                              </Link>
                            ))}
                          </Wrap>
                        );
                      }
                    } catch {
                      // Ignore parsing errors
                    }
                    return null;
                  })()}
                </Table.Cell>
                <Table.Cell>{rec.notes}</Table.Cell>
              </Table.Row>
            ))
          ) : (
            <Table.Row>
              <Table.Cell colSpan={4}>
                <Text textAlign="center" py={10}>
                  No recommendations found for this episode.
                </Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}
